import type { SyntheticEvent } from 'react';

/**
 * Drawer registry module that manages the state of drawers in the application.
 * Uses a module pattern to encapsulate private state and expose a public API.
 */
export const drawerRegistry = (() => {
  // Private variables
  const activeIds = new Set<string>();
  const listeners = new Set<() => void>();

  // Map to store the element that triggered each drawer
  const triggerElements = new Map<string, HTMLElement>();

  /**
   * Notify all registered listeners about a state change.
   * @private
   */
  function notifyListeners(): void {
    listeners.forEach((listener) => listener());
  }

  // Public API
  return {
    /**
     * Subscribe to changes in the drawer registry.
     * @param listener - Function to call when drawer state changes
     * @returns Function to unsubscribe the listener
     */
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /**
     * Open a drawer with the given ID.
     * If the drawer is already open, this is a no-op.
     * @param id - Unique identifier for the drawer
     * @returns Whether the drawer was activated
     */
    open(id?: string, event?: SyntheticEvent): boolean {
      if (!id || id.trim() === '') {
        return false;
      }

      if (activeIds.has(id)) {
        return false;
      }

      if (event?.currentTarget instanceof HTMLElement) {
        triggerElements.set(id, event.currentTarget);
      }

      activeIds.add(id);
      notifyListeners();
      return true;
    },

    /**
     * Close a drawer with the given ID.
     * If the drawer is not open, this is a no-op.
     * @param id - Unique identifier for the drawer
     */
    close(id?: string): void {
      if (!id || id.trim() === '') {
        return;
      }

      if (activeIds.has(id)) {
        activeIds.delete(id);

        // Return focus to the trigger element
        const element = triggerElements.get(id);
        if (element && document.body.contains(element)) {
          setTimeout(() => element.focus(), 0);
        }
        triggerElements.delete(id);

        notifyListeners();
      }
    },

    /**
     * Get a list of all currently active drawer IDs.
     * @returns Array of active drawer IDs
     */
    getActiveIds(): Array<string> {
      return Array.from(activeIds);
    },
  };
})();
